import React from 'react';
import {
  RendererFactory, SurveyModel,
} from 'survey-core';
import { ReactQuestionFactory, SurveyQuestionSignaturePad } from 'survey-react-ui';
import { Box } from '@mui/material';
import { SignerInvite } from './signer-invite';
import {
  Submission, SurveySettings, SIGNED_SUBMISSION_STATUSES,
} from '../../../types';
import { requestSigner } from '../../../api/signatures/request-signer';
import { deleteSigner } from '../../../api/signatures/delete-signer';

const SIGNATURE_PAD_QUESTION_TYPE = 'signaturepad';

interface RegisterSignaturePadWithInviteQuestionProps {
  settings: SurveySettings;
  submission?: Submission;
  isSigner?: boolean;
}

export const registerSignaturePadWithInviteQuestion = ({
  settings,
  submission,
  isSigner = false,
}: RegisterSignaturePadWithInviteQuestionProps) => {
  if (!submission || isSigner) return;
  if (SIGNED_SUBMISSION_STATUSES.has(submission.status)) return;

  class SignaturePadWithInviteQuestion extends SurveyQuestionSignaturePad {
    constructor (props: any) {
      super(props);
      this.handleInviteSigner = this.handleInviteSigner.bind(this);
      this.handleCancelInvite = this.handleCancelInvite.bind(this);
    }

    get surveyModel () {
      return this.question.getSurvey() as SurveyModel;
    }

    async handleInviteSigner (email: string) {
      // Call the API to invite the signer using the existing function
      if (!submission) {
        throw new Error('Submission does not exist');
      }

      await requestSigner(email, settings.urlSlug, submission.publicId);

      // Set the survey to readOnly, so only the signer can edit unless the invite is canceled
      this.surveyModel.readOnly = true;

      // Update submission.signer so other SignerInvite components also display the signer email
      submission.signer = {
        email,
        isInitialUser: true,
        isVerified: false,
      };

      return true;
    }

    async handleCancelInvite () {
      if (!submission) {
        throw new Error('Submission does not exist');
      }

      await deleteSigner(settings.urlSlug, submission.publicId);

      // Set the survey back to edit mode
      this.surveyModel.readOnly = false;

      submission.signer = undefined;

      return true;
    }

    renderElement () {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {submission && (
            <SignerInvite
              isSignerVerified={submission?.signer?.isVerified}
              signerEmail={submission.signer?.email}
              onCancel={this.handleCancelInvite}
              onInvite={this.handleInviteSigner}
            />
          )}
          {super.renderElement()}
        </Box>
      );
    }
  }

  ReactQuestionFactory.Instance.registerQuestion(
    'sv-signaturepad-with-invite',
    function (props) {
      return React.createElement(SignaturePadWithInviteQuestion, props);
    },
  );

  RendererFactory.Instance.registerRenderer(
    SIGNATURE_PAD_QUESTION_TYPE,
    'signaturepad-with-invite',
    'sv-signaturepad-with-invite',
    true,
  );
};
